/**
 * Field Registry - Central registry for all form field components
 * Maps field type strings to React components.
 */

import * as BasicFields from './basic';
import * as SelectFields from './select';
import * as CustomFields from './custom';
import * as ColorFields from './color';
import * as DirFields from './dir';

// Resolved lazily: ArrayObjectField imports this module, so an eager table would
// capture CustomFields.ArrayObjectField as undefined during the import cycle.
const FIELD_RESOLVERS = {
    text: () => BasicFields.TextField,
    password: () => BasicFields.PasswordField,
    number: () => BasicFields.NumberField,
    textarea: () => BasicFields.TextareaField,
    float: () => BasicFields.FloatField,
    hidden: () => BasicFields.HiddenField,
    notice: () => BasicFields.NoticeField,
    check_box: () => SelectFields.CheckboxField,
    toggle: () => SelectFields.CheckboxField,
    dropdown: () => SelectFields.DropdownField,
    segmented: () => SelectFields.SegmentedField,
    primary_source: () => SelectFields.PrimarySourceField,
    multiselect: () => SelectFields.MultiSelectField,
    json: () => CustomFields.JsonField,

    color: () => ColorFields.ColorField,
    color_list: () => ColorFields.ColorListField,

    dir: () => DirFields.DirField,
    dirlist: () => DirFields.DirListField,
    dirlist_dragdrop: () => DirFields.DirListDragDropField,
    dirlist_options: () => DirFields.DirListOptionsField,

    holiday_schedule: () => SelectFields.DateRangeField,

    presets: () => CustomFields.PresetsField,

    array: () => CustomFields.TagInputField,
    object_array: () => CustomFields.ArrayObjectField,
    'array-object': () => CustomFields.ArrayObjectField,
    instances: () => CustomFields.InstancesField,
    plex_scope: () => CustomFields.InstancesField,
    plex_library_exclude: () => CustomFields.PlexLibraryExcludeField,
    instance_dropdown: () => SelectFields.DropdownField,
    schedule: () => CustomFields.ScheduleField,
    tag_input: () => CustomFields.TagInputField,
    tag_display: () => CustomFields.TagInputField,
    media_info_display: () => CustomFields.MediaInfoDisplayField,
    media_display: () => CustomFields.MediaDisplayField,
    dir_picker: () => CustomFields.DirPickerField,
    poster: () => CustomFields.PosterField,
    action_button: () => CustomFields.ActionButtonField,
};

const FIELD_COMPONENTS = {};

const resolveField = fieldType => {
    if (FIELD_COMPONENTS[fieldType]) return FIELD_COMPONENTS[fieldType];
    const resolver = FIELD_RESOLVERS[fieldType];
    if (!resolver) return null;
    const component = resolver();
    if (component) FIELD_COMPONENTS[fieldType] = component;
    return component || null;
};

/**
 * Fallback component for truly unknown field types (not in registry at all)
 */
const UnknownFieldType = ({ field }) => {
    const inputId = `field-${field.key}`;

    return (
        <>
            <label className="text-sm font-medium text-fg-muted">
                {field.label}
                {field.required && <span className="text-error ml-1">*</span>}
            </label>

            <div className="mt-2">
                <div className="p-4 bg-error/20 border border-error rounded-lg text-center text-error text-sm">
                    <strong>Unknown field type &quot;{field.type}&quot;</strong>
                    <br />
                    <small>This field type is not recognized by the system</small>
                </div>
            </div>

            {field.description && (
                <div id={`${inputId}-description`} className="text-sm text-fg-subtle mt-1">
                    {field.description}
                </div>
            )}
        </>
    );
};

// Define which field types are IMPLEMENTED
const IMPLEMENTED_FIELD_TYPES = new Set([
    'text',
    'password',
    'number',
    'textarea',
    'float',
    'hidden',
    'notice',
    'check_box',
    'toggle',
    'dropdown',
    'segmented',
    'primary_source',
    'multiselect',
    'json',
    'color',
    'color_list',
    'dir',
    'dir_picker',
    'dirlist',
    'dirlist_dragdrop',
    'dirlist_options',
    'presets',
    'holiday_schedule',
    'array',
    'object_array',
    'array-object',
    'instances',
    'plex_scope',
    'plex_library_exclude',
    'instance_dropdown', // Now implemented using DropdownField
    'tag_input',
    'tag_display',
    'schedule',
    'action_button',
]);

// The set as it stands before any register() call: unregister() restores exactly this.
const RESOLVER_IMPLEMENTED_FIELD_TYPES = new Set(IMPLEMENTED_FIELD_TYPES);

/**
 * FieldRegistry class provides methods to register, retrieve, and manage field components
 */
export class FieldRegistry {
    /**
     * Get a field component by type
     * @param {string} fieldType - The field type string
     * @returns {React.Component} The field component or UnknownFieldType fallback
     */
    static getField(fieldType) {
        const component = resolveField(fieldType);
        if (!component) {
            console.warn(`[FieldRegistry] Unknown field type: ${fieldType}`);
            return UnknownFieldType;
        }
        return component;
    }

    /**
     * Register a new field component
     * @param {string} fieldType - The field type string
     * @param {React.Component} component - The React component to register
     */
    static register(fieldType, component) {
        if (FIELD_COMPONENTS[fieldType]) {
            console.warn(`[FieldRegistry] Overriding existing field type: ${fieldType}`);
        }
        FIELD_COMPONENTS[fieldType] = component;
        // A registered component IS the implementation; without this the dev page calls it a placeholder.
        IMPLEMENTED_FIELD_TYPES.add(fieldType);
    }

    /**
     * Check if a field type is registered
     * @param {string} fieldType - The field type string
     * @returns {boolean} True if field type exists
     */
    static hasField(fieldType) {
        return fieldType in FIELD_RESOLVERS || fieldType in FIELD_COMPONENTS;
    }

    /**
     * Get all registered field types (including placeholders)
     * @returns {string[]} Array of all field type strings
     */
    static getFieldTypes() {
        return Array.from(
            new Set([...Object.keys(FIELD_RESOLVERS), ...Object.keys(FIELD_COMPONENTS)])
        );
    }

    /**
     * Get only implemented field types (excludes placeholders)
     * @returns {string[]} Array of implemented field type strings
     */
    static getWorkingFieldTypes() {
        return Array.from(IMPLEMENTED_FIELD_TYPES);
    }

    /**
     * Get only placeholder field types
     * @returns {string[]} Array of placeholder field type strings
     */
    static getPlaceholderFieldTypes() {
        return Object.keys(FIELD_RESOLVERS).filter(type => !IMPLEMENTED_FIELD_TYPES.has(type));
    }

    /**
     * Check if a field type is implemented (not a placeholder)
     * @param {string} fieldType - The field type string
     * @returns {boolean} True if field type is implemented
     */
    static isWorkingFieldType(fieldType) {
        return IMPLEMENTED_FIELD_TYPES.has(fieldType);
    }

    /**
     * Remove a field type from the registry
     * @param {string} fieldType - The field type string
     */
    static unregister(fieldType) {
        delete FIELD_COMPONENTS[fieldType];
        // Only registration marked it implemented; a placeholder resolver goes back to placeholder.
        if (!RESOLVER_IMPLEMENTED_FIELD_TYPES.has(fieldType)) {
            IMPLEMENTED_FIELD_TYPES.delete(fieldType);
        }
    }
}

/**
 * React hook to get a field component
 * @param {string} fieldType - The field type string
 * @returns {React.Component|null} The field component or null if not found
 */
export function useFieldComponent(fieldType) {
    return FieldRegistry.getField(fieldType);
}

/**
 * Default export for convenience
 */
export default FieldRegistry;
